import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getCommunicationServiceAccounts,
  getWorkspaceCommunicationCredits,
  ICommunicationServiceAccount,
  refreshAccountsWithApi,
  refreshZoom,
} from 'wisestep-api';
import { ZoomLoader } from 'wisestep-base';

import { AccessControl } from '../../components';
import { IOption } from '../../components/SettingsIndex';
import { useWorkspaceContext } from '../../context';
import { SmsConfigAction, useSmsConfigAccessControl } from '../../hooks/useSmsConfigAccessControl';
import AppSettingsLayout from '../appsettings';
import ExtensionMappingListing from '../extensionMappings/components/ExtensionMappingListing';
import PayementConfirmation from '../paymentFlow/components/PayementConfirmation';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';

import AddRingCentralAccount from './components/AddRingCentralAccount';
import AddTelnyxAccount from './components/AddTelnyxAccount';
import AddTwilioAccount from './components/AddTwilioAccount';
import AddZoomAccount from './components/AddZoomAccount';
import CommunicationAccounts from './components/CommunicationAccounts';
import CommunicationLogs from './components/CommunicationLogs';
import ZoomAccounts from './components/ZoomAccounts';
import ZoomConfirmationModal from './components/ZoomConfirmationModal';

const SMSConfiguration = () => {
  const admin = useWorkspaceContext();
  const [loader, setLoader] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<number>(0);
  const [showPostPaymentModal, setShowPostPaymentModal] = useState(false);
  const [selectedSubOption, setSelectedSubOption] = useState<number>(0);
  const params = useLocation();
  const navigate = useNavigate();
  const [credit, setCredit] = useState<number>(0);

  const [wisestepSmsAccounts, setWisestepSmsAccounts] = useState<ICommunicationServiceAccount[]>(
    []
  );

  const [twilioAccounts, setTwilioAccounts] = useState<ICommunicationServiceAccount[]>([]);

  const [telnyxAccounts, setTelnyxAccounts] = useState<ICommunicationServiceAccount[]>([]);

  const [ringCentralAccounts, setRingCentralAccounts] = useState<ICommunicationServiceAccount[]>(
    []
  );

  const [zoomAccounts, setZoomAccounts] = useState<ICommunicationServiceAccount[]>([]);
  const { access } = useSmsConfigAccessControl([
    SmsConfigAction.MANAGE_SMS_CONFIGURATION,
    SmsConfigAction.VIEW_SMS_CONFIGURATION,
    SmsConfigAction.DELETE_SMS_CONFIGURATION,
    SmsConfigAction.MANAGE_SMS_USERS,
  ]);
  const [manageSmsConfig, viewSmsConfig, deleteSmsConfig, manageSmsUsers] = access;
  const hasAccessToListing =
    admin.owns || manageSmsConfig || viewSmsConfig || deleteSmsConfig || manageSmsUsers;

  const smsOptions: IOption[] = [
    {
      label: 'Wisestep SMS',
      subOptions: [
        {
          label: 'SMS Accounts',
        },
        {
          label: 'Logs',
        },
      ],
    },
    {
      label: 'Integrations',
      subOptions: [
        {
          label: 'Twilio',
        },
        {
          label: 'Telnyx',
        },
        {
          label: 'Ring Central',
        },
        {
          label: 'Zoom',
        },
      ],
    },
    {
      label: 'Extension Mapping',
      subOptions: [],
    },
  ];
  const smsOptionsForUser: IOption[] = [
    {
      label: 'Wisestep SMS',
      subOptions: [
        {
          label: 'SMS Accounts',
        },
        {
          label: 'Logs',
        },
      ],
    },
  ];

  useEffect(() => {
    params.search.indexOf('?success=') > -1
      ? setShowPostPaymentModal(true)
      : setShowPostPaymentModal(false);
  }, [params.search]);

  const getCredits = useCallback(async () => {
    const response = await getWorkspaceCommunicationCredits();
    setCredit(response.data.credit);
  }, []);

  const sortAccounts = useCallback((data: ICommunicationServiceAccount[]) => {
    data.map((account) => {
      if (account.serviceName === 'WS_TELNYX') {
        setWisestepSmsAccounts([account]);
      } else if (account.serviceName === 'TWILIO') {
        setTwilioAccounts([account]);
      } else if (account.serviceName === 'TELNYX') {
        setTelnyxAccounts([account]);
      } else if (account.serviceName === 'RINGCENTRAL') {
        setRingCentralAccounts((prev) => [...prev, account]);
      } else if (account.serviceName === 'ZOOM') {
        setZoomAccounts([account]);
        const openModal = JSON.parse(localStorage.getItem('modalRendered') ?? 'true');
        if (!openModal) {
          setModalOpen(true);
          markModalState();
        }
      }
    });
  }, []);

  const refresh = async () => {
    const response = await refreshAccountsWithApi();
    sortAccounts(response.data);
  };

  const refreshZoomAccounts = async () => {
    setLoader(true);
    const accountIdList = zoomAccounts.map((zoom) => zoom.serviceAccountId);
    await refreshLocalStorage();
    await refreshZoom({ accountIds: accountIdList });
    setLoader(false);
    setModalOpen(true);
  };

  const getAccounts = useCallback(async () => {
    const response = await getCommunicationServiceAccounts();
    localStorage.setItem('communicationAccounts', JSON.stringify(response.data));
    getAccountsFromLocalStorage();
    sortAccounts(response.data);
  }, [sortAccounts]);

  const refreshLocalStorage = async () => {
    localStorage.removeItem('communicationAccounts');
    getAccounts();
  };

  const getAccountsFromLocalStorage = useCallback(() => {
    const accounts = JSON.parse(localStorage.getItem('communicationAccounts') ?? '[]');
    if (accounts.length === 0) getAccounts();
    else sortAccounts(accounts);
  }, [getAccounts, sortAccounts]);

  const markModalState = async () => localStorage.setItem('modalRendered', JSON.stringify(true));

  const unMarkModalState = async () => localStorage.setItem('modalRendered', JSON.stringify(false));

  useEffect(() => {
    getCredits();
    getAccountsFromLocalStorage();
  }, [getAccountsFromLocalStorage, getCredits]);

  return (
    <>
      {showPostPaymentModal && (
        <PayementConfirmation
          onClose={() => navigate('/sms-configuration')}
          success={params.search.indexOf('?success=true') > -1 ? true : false}
          updated={params.search.indexOf('?success=true') > -1 ? true : false}
        />
      )}
      <AppSettingsLayout
        title="Communication Account Configuration"
        breadCrumnbsList={getSettingBreadCrumbList(
          ISettingBreadCrumbTypes.APPS_AND_INTEGRATION,
          Pages['COMMUNICATION_ACCOUNT_CONFIGURATION']
        )}
        options={admin.owns ? smsOptions : smsOptionsForUser}
        getSelectedOption={(option) => setSelectedOption(option)}
        getSelectedSubOption={(subOption) => setSelectedSubOption(subOption)}
      >
        {selectedOption === 0 &&
          selectedSubOption === 0 &&
          (admin.owns || hasAccessToListing ? (
            <CommunicationAccounts
              credit={credit}
              communicationAccounts={wisestepSmsAccounts}
              refresh={refresh}
              refreshLocalStorage={refreshLocalStorage}
              getAccounts={getAccounts}
            />
          ) : (
            <AccessControl />
          ))}
        {selectedOption === 0 &&
          selectedSubOption === 1 &&
          (admin.owns || hasAccessToListing ? <CommunicationLogs /> : <AccessControl />)}
        {selectedOption === 1 &&
          selectedSubOption === 0 &&
          (twilioAccounts.length > 0 && hasAccessToListing ? (
            <CommunicationAccounts
              deleteable={true}
              communicationAccounts={twilioAccounts}
              refresh={refresh}
              refreshLocalStorage={refreshLocalStorage}
              getAccounts={getAccounts}
            />
          ) : hasAccessToListing ? (
            <AddTwilioAccount getAccounts={getAccounts} />
          ) : (
            <AccessControl />
          ))}
        {selectedOption === 1 &&
          selectedSubOption === 1 &&
          (telnyxAccounts.length > 0 && hasAccessToListing ? (
            <CommunicationAccounts
              deleteable={true}
              communicationAccounts={telnyxAccounts}
              refresh={refresh}
              refreshLocalStorage={refreshLocalStorage}
              getAccounts={getAccounts}
            />
          ) : hasAccessToListing ? (
            <AddTelnyxAccount getAccounts={getAccounts} />
          ) : (
            <AccessControl />
          ))}
        {selectedOption === 1 &&
          selectedSubOption === 2 &&
          (ringCentralAccounts.length > 0 && hasAccessToListing ? (
            <CommunicationAccounts
              deleteable={true}
              communicationAccounts={ringCentralAccounts}
              refresh={refresh}
              refreshLocalStorage={refreshLocalStorage}
              getAccounts={getAccounts}
            />
          ) : hasAccessToListing ? (
            <AddRingCentralAccount />
          ) : (
            <AccessControl />
          ))}
        {selectedOption === 1 &&
          selectedSubOption === 3 &&
          (zoomAccounts.length > 0 && hasAccessToListing ? (
            <ZoomAccounts
              deleteable={true}
              communicationAccounts={zoomAccounts}
              refresh={refresh}
              refreshLocalStorage={refreshLocalStorage}
              getAccounts={getAccounts}
              refreshZoomAccounts={refreshZoomAccounts}
              setZoomAccounts={setZoomAccounts}
            />
          ) : loader ? (
            <ZoomLoader />
          ) : hasAccessToListing ? (
            <AddZoomAccount
              unMarkModalState={unMarkModalState}
              getAccounts={getAccounts}
              setModal={setModalOpen}
            />
          ) : (
            <AccessControl />
          ))}
        {selectedOption === 2 && <ExtensionMappingListing />}
        {modalOpen && <ZoomConfirmationModal modal={modalOpen} setModal={setModalOpen} />}
      </AppSettingsLayout>
    </>
  );
};

export default SMSConfiguration;
